<template>
    <div id="organization-profile">
        <Article :title="organization.name">
            <ul>
                <li>
                    <span>CVR {{ organization.cvr || $t("notOnRecord") }}</span>
                </li>
                <li>
                    <div class="pi pi-home"></div>
                    <span>{{ organization.address || $t("notOnRecord") }}</span>
                </li>
                <li>
                    <span class="pi pi-globe"></span>
                    <span>{{ sanitizedWebsiteLink || $t("notOnRecord") }}</span>
                    <a v-if="sanitizedWebsiteLink" class="pi pi-external-link"
                       :href="sanitizedWebsiteLink" target="_blank"></a>
                </li>
            </ul>

            <AssociatesList associates-role="Admin"
                            :has-admin-role="isAdmin"
                            :organization-id="organization.id"
                            :associates="administrators"
                            :associates-role-label="$t('administrators')"
                            :associates-role-description="$t('administratorDescription')"
                            @associate-added="addAssociate"
                            @associate-removed="removeAssociate"/>

            <AssociatesList associates-role="Collaborator"
                            :has-admin-role="isAdmin"
                            :organization-id="organization.id"
                            :associates="collaborators"
                            :associates-role-label="$t('collaborators')"
                            :associates-role-description="$t('collaboratorDescription')"
                            @associate-added="addAssociate"
                            @associate-removed="removeAssociate"/>

            <AssociatesList associates-role="Guest"
                            :has-admin-role="isAdmin"
                            :organization-id="organization.id"
                            :associates="guests"
                            :associates-role-label="$t('guests')"
                            :associates-role-description="$t('guestDescription')"
                            @associate-added="addAssociate"
                            @associate-removed="removeAssociate"/>
        </Article>
    </div>
</template>

<script>
    import AssociatesList from "./AssociatesList";

    export default {
        components: {
            AssociatesList,
        },
        props: {
            isAdmin: Boolean,
            organization: {
                id: String,
                name: String,
                cvr: String,
                address: String,
                website: String,
                associates: Array
            }
        },
        methods: {
            addAssociate(associate) {
                const updatedOrganization = {
                  ...this.organization,
                  associates: this.organization.associates
                    .filter(a => a.id !== associate.id)
                    .concat([associate])
                };

                this.$emit('update:organization', updatedOrganization);
            },
            removeAssociate(associateId) {
                const updatedOrganization = {
                  ...this.organization,
                  associates: this.organization.associates
                    .filter(associate => associate.id !== associateId)
                };

                this.$emit('update:organization', updatedOrganization);
            }
        },
        computed: {
            administrators() {

                if (this.organization.associates) {
                    return this.organization.associates.filter(associate => associate.role === "Admin");
                }

                return [];
            },
            collaborators() {

                if (this.organization.associates) {
                    return this.organization.associates.filter(associate => associate.role === "Collaborator");
                }

                return [];
            },
            guests() {

                if (this.organization.associates) {
                    return this.organization.associates.filter(associate => associate.role === "Guest");
                }

                return [];
            },
            sanitizedWebsiteLink() {
                if (!this.organization.website) {
                    return null;
                }

                if (this.organization.website.toLowerCase().match('https?://')) {
                    return this.organization.website;
                }

                return 'http://' + this.organization.website;
            }
        }
    };
</script>

<style lang="scss">
    #organization-profile {

        ul {
            padding: 0;
            list-style-type: none;
            margin-top: 1em;

            * {
                margin-left: .4em;
                font-size: 1.2em;
            }
        }

        .card {
            margin: 0;
            padding: 0;
        }

        .p-dataview-content {
            border: 0;
        }

        .p-dataview-content > .p-grid > div {
            border: 0;
        }
    }
</style>

<i18n lang="yaml" locale="en">
  notOnRecord: "not on record"
  administrators: "Administrators"
  administratorDescription: "An administrator can manage all organization resources such as installation and chemicals registration management, reporting and associates management - i.e. can assign and remove administrators, collaborators and guests."
  collaborators: "Collaborators"
  collaboratorDescription: "Collaborators can manage organization resources with the exception of associates - i.e. cannot invite new associates to an organization."
  guests: "Guests"
  guestDescription: "Guests have read-only access to the organization's resources. Suitable for external to the organization personnel that would require temporary access, such as auditors."
</i18n>

<i18n lang="yaml" locale="dk">
  notOnRecord: "Ikke registreret"
  administrators: "Administratorer"
  administratorDescription: "En administrator har råderet over alle organisationens resourser, herunder måler- og kemikalie regstreringer. En administrator kan tildele og fjerne administratore, medarbejdere og gæster. "
  collaborators: "Medarbejdere"
  collaboratorDescription: "En medarbejder kan styre organisationens resourser, dog ikke adgang for andre brugere. En medarbejder kan ikke invitere nye brugere til organisationen."
  guests: "Gæster"
  guestDescription: "Gæster har read-only adgang til organisatioens resourser. Gæste-kontoen bør benyttes til personer med tidsbegrænset adgang (auditore) eller for personale med afgrænset ressortområde (eks. tekniske medarbejdere). "
</i18n>
